import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, Stack, Typography } from '@mui/material';
import type { ObjectivePower } from './magnification';
import type { PracticalMode, SpecimenSlide } from './specimens';

interface StepGuideProps {
  mode: PracticalMode;
  slide: SpecimenSlide;
  objective: ObjectivePower;
  focusOffsetUm: number;
  stageClipsOn: boolean;
  lampOn: boolean;
  lamp: number;
  diaphragm: number;
  observationCount: number;
  completedSteps: string[];
  onCompletedStepsChange: (steps: string[]) => void;
}

const StepGuide: React.FC<StepGuideProps> = ({ mode, slide, objective, focusOffsetUm, stageClipsOn, lampOn, lamp, diaphragm, observationCount, completedSteps, onCompletedStepsChange }) => {
  const [showHints, setShowHints] = useState(false);
  const steps = useMemo(() => [
    { id: 'secure-slide', label: 'Place the slide on the stage and secure it with the clips.', valid: stageClipsOn, hint: 'Close both stage clips so the preparation cannot move while focusing.' },
    { id: 'low-power', label: 'Begin with the 4× objective and coarse focus.', valid: objective === 4 && Math.abs(focusOffsetUm) <= 2, hint: 'Rotate the 4× objective into place, then bring the specimen close to focus with coarse adjustment.' },
    { id: 'fine-focus', label: 'Switch to a higher objective and sharpen with fine focus.', valid: objective >= 10 && Math.abs(focusOffsetUm) <= 1, hint: 'Select 10× or 40× and use only small focus changes at higher power.' },
    { id: 'illumination', label: 'Balance lamp brightness and diaphragm for contrast.', valid: lampOn && lamp >= 0.2 && diaphragm >= 0.2 && diaphragm <= 0.9, hint: 'Open the iris enough for a bright field without washing out specimen detail.' },
    { id: 'record-evidence', label: `Identify structures and record an observation for ${slide.name}.`, valid: observationCount > 0, hint: 'Record what you can see and support the description with a captured field image or measurement.' },
  ], [stageClipsOn, objective, focusOffsetUm, lampOn, lamp, diaphragm, slide.name, observationCount]);
  const checkedCount = steps.filter((step) => completedSteps.includes(step.id)).length;

  const toggleStep = (id: string, checked: boolean, valid: boolean) => {
    if (checked && !valid) { setShowHints(true); return; }
    onCompletedStepsChange(checked ? [...completedSteps, id] : completedSteps.filter((stepId) => stepId !== id));
  };

  return <Paper className="micro-panel guide-panel" elevation={0}>
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1}>
      <Box><Typography variant="h6" fontWeight={800}>Guided practical · {mode.toUpperCase()}</Typography><Typography variant="body2" color="text.secondary">{checkedCount} of {steps.length} validated steps</Typography></Box>
      <Button variant="outlined" onClick={() => setShowHints((value) => !value)} aria-expanded={showHints}>Show correct setup</Button>
    </Stack>
    <div className="guide-progress" role="progressbar" aria-valuenow={checkedCount} aria-valuemin={0} aria-valuemax={steps.length}><span style={{ width: `${(checkedCount / steps.length) * 100}%` }} /></div>
    {showHints && <Alert severity="info" sx={{ mb: 1 }}>Setup hints are shown beside any step that has not yet been completed successfully. These prompts guide the setup without giving away specimen identifications.</Alert>}
    <Stack spacing={0.2}>
      {steps.map((step) => {
        const checked = completedSteps.includes(step.id);
        return <div key={step.id} className={`guide-step ${checked ? 'guide-step-complete' : ''}`}>
          <FormControlLabel control={<Checkbox checked={checked} onChange={(event) => toggleStep(step.id, event.target.checked, step.valid)} inputProps={{ 'aria-label': step.label }} />} label={step.label} />
          {showHints && !checked && <Typography className="guide-hint" variant="caption">Hint: {step.hint}</Typography>}
        </div>;
      })}
    </Stack>
  </Paper>;
};

export default StepGuide;
